import {FilterGroup, ViewBy} from '@/domain/services/Clas';
import {Sources} from 'mapbox-gl';
import {VIEW_BY_OPTIONS} from '@/config';
import {Profile} from '@/domain/entities/Profile';

const GEOSERVER_URL = process.env.GEOSERVER_URL;
const GEOSERVER_CETEMAS = process.env.GEOSERVER_CETEMAS;

const sourcesConstructor: (
  overlayIds: Array<string>, 
  viewBy: ViewBy, 
  filters: FilterGroup,
  year?: number,
  profile?: Profile,
) => Sources = (
  overlayIds: Array<string>, 
  viewBy: ViewBy, 
  filters: FilterGroup,
  year?: number,
  profile?: Profile,
) => {

  const profileUuid = profile?.uuid;
  const parcelsCql = createCQLForParcels(filters);
  const precinctsCql = createCQLForPrecinct(filters);

  const style = viewBy === VIEW_BY_OPTIONS.USE
    ? 'precinct_by_use'
    : viewBy === VIEW_BY_OPTIONS.CROP
      ? 'precinct_by_crop'
      : viewBy === VIEW_BY_OPTIONS.USER
        ? 'precinct_by_user'
        : 'precinct_default';

  const overlay = overlayIds
    ? Object.fromEntries(
      overlayIds.map(overlayId => [
        overlayId,
        {
          type: 'raster',
          tiles: [
            `${GEOSERVER_CETEMAS}/go_geprocar/wms?` +
            'service=WMS&' +
            'version=1.1.0&' +
            'request=GetMap&' +
            'bbox={bbox-epsg-3857}&' +
            'styles=&' +
            'format=image/png&' +
            'transparent=true&' +
            `layers=${overlayId}&` +
            'width=768&' +
            'height=330&' +
            'srs=EPSG:3857'
          ],
          bounds: [-7.1191, 42.8477, -4.4934, 43.75],
          minzoom: 7,
          maxzoom: 14
        }
      ])
    )
    : {};

  return {
    catastro: {
      type: 'raster',
      tiles: ['https://ovc.catastro.meh.es/Cartografia/WMS/ServidorWMS.aspx?' +
      'bbox={bbox-epsg-3857}&' +
      'format=image/png&' +
      'service=WMS&' +
      'request=GetMap&' +
      'srs=EPSG:3857&' +
      'width=256&' +
      'height=256&' +
      'layers=catastro'
      ],
      bounds: [-7.1191, 42.8477, -4.4934, 43.75],
      minzoom: 7,
      maxzoom: 14
    },
    parcel: {
      type: 'raster',
      tiles: [
        `${GEOSERVER_URL}/wms?` +
        'service=WMS&' +
        'version=1.1.1&' +
        'request=GetMap&' +
        'layers=clas:parcel&' +
        'styles=parcel_default&' +
        (year ? `time=${year}&` : '') +
        (parcelsCql ? `CQL_FILTER=${encodeURIComponent(parcelsCql)}&` : '') +
        'bbox={bbox-epsg-3857}&' +
        'width=256&' +
        'height=256&' +
        'srs=EPSG:3857&' +
        'format=image/png&' +
        'transparent=true'
      ],
      minzoom: 8,
    },
    precinct: {
      type: 'raster',
      tiles: [
        `${GEOSERVER_URL}/wms?` +
          'service=WMS&' +
          'version=1.1.1&' +
          'request=GetMap&' +
          'bbox={bbox-epsg-3857}&' +
          `styles=${style}&` +
          (year ? `time=${year}&` : '') +
          (precinctsCql ? `CQL_FILTER=${encodeURIComponent(precinctsCql)}&` : '') +
          (viewBy === ViewBy.USER ? `env=user_uuid:${profileUuid}&` : '') +
          'format=image/png&' +
          'transparent=true&' +
          'layers=clas:precinct&' +
          'width=768&' +
          'height=330&' +
          'srs=EPSG:3857'
      ],
      minzoom: 8,
    },
    redNatura: {
      type: 'raster',
      tiles: [
        'https://wms.mapama.gob.es/sig/Biodiversidad/RedNatura/wms.aspx?' +
        'bbox={bbox-epsg-3857}&' +
        'styles=default&' +
        'format=image/png&' +
        'service=WMS&' +
        'version=1.1.1&' +
        'request=GetMap&' +
        'srs=EPSG:3857&' +
        'transparent=true&' +
        'width=256&' +
        'height=256&' +
        'layers=PS.ProtectedSite'
      ],
      bounds: [-7.1191, 42.8477, -4.4934, 43.75],
      minzoom: 7,
      maxzoom: 14
    },
    protection: {
      type: 'raster',
      tiles: [
        'https://wms.mapama.gob.es/sig/Biodiversidad/IEAAPP/wms.aspx?' +
        'bbox={bbox-epsg-3857}&n' +
        'styles=default&n' +
        'format=image/png&' +
        'service=WMS&' +
        'version=1.1.1&' +
        'request=GetMap&' +
        'srs=EPSG:3857&' +
        'transparent=true&' +
        'width=256&' +
        'height=256&' +
        'layers=PS.ProtectedSite'
      ],
      bounds: [-7.1191, 42.8477, -4.4934, 43.75],
      minzoom: 7,
      maxzoom: 14
    },
    ...overlay
  };
};

export default sourcesConstructor;

const buildCqlFilter = (filters: Partial<FilterGroup>) => {
  const cql = Object.entries(filters)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .filter(([_, value]) =>
      value !== undefined &&
      value !== null &&
      value !== ''
    )
    .map(([key, value]) => {
      const cqlKey = CQL_KEY_MAP[key] ?? key;
      return `${cqlKey}='${value}'`;
    })
    .join(' AND ');

  return cql.length > 0 ? cql : undefined;

};

const createCQLForParcels = (filters: FilterGroup) => {
  const {userUuid, municipalityUuid} = filters;
  return buildCqlFilter({userUuid, municipalityUuid});
};

const createCQLForPrecinct = (filters: FilterGroup) => {
  return buildCqlFilter(filters);
};

const CQL_KEY_MAP: Record<string, string> = {
  userUuid: 'user_uuid',
  municipalityUuid: 'municipality_uuid',
  useUuid: 'use_uuid',
  cropUuid: 'crop_uuid',
  protectionType: 'protection_type',
  enpType: 'enp_type',
  rnType: 'rn_type',
  isMab: 'is_mab'
};